import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ClipLoader, PulseLoader } from 'react-spinners';
import moment from 'moment';
import { EnvelopeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

import { useToast } from 'context/toastContext';
import { EMAIL_INVITES_ENABLED } from 'utils/features';
import { getEventInvites, sendEventInvite } from '../../../redux/actions/events';

import { cx } from './atoms';

// Same loose check as the API — the real test is whether the mail arrives.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const STATUS_STYLE = {
  sent: 'bg-gray-100 text-gray-600',
  registered: 'bg-blue-50 text-blue-700',
  booked: 'bg-emerald-50 text-emerald-700',
};

// API error code → message. Unknown codes fall back to a generic line.
const errorMessage = (t, data) => {
  const code = data?.code;
  if (code === 'INVITE_COOLDOWN' && data?.retryAt) {
    return t('dashboard.em.invite.err_INVITE_COOLDOWN_at', {
      when: moment(data.retryAt).fromNow(),
    });
  }
  return code
    ? t(`dashboard.em.invite.err_${code}`, t('dashboard.em.invite.err_generic'))
    : t('dashboard.em.invite.err_generic');
};

// The promoter's "Invite brands" tab: email invites, shown only once
// EMAIL_INVITES_ENABLED is on. Sharing the event (link, Instagram images, QR
// code) is on the My Events cards instead.
export default function InviteTab({ event }) {
  return (
    <div className="max-w-3xl mx-auto w-full">
      {EMAIL_INVITES_ENABLED && <EmailInvites event={event} />}
    </div>
  );
}

function EmailInvites({ event }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const addToast = useToast();
  const eventId = event?.id;

  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [data, setData] = useState({ invites: [], canInvite: true, blocker: null });
  const [loading, setLoading] = useState(true);

  const lang = i18n.language?.startsWith('pt') ? 'pt' : 'en';

  const load = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await dispatch(getEventInvites(eventId));
      setData({
        invites: res?.invites || [],
        canInvite: res?.canInvite !== false,
        blocker: res?.blocker || null,
      });
    } catch (err) {
      console.error('Failed to load invites:', err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, eventId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const send = async (address) => {
    const res = await dispatch(sendEventInvite(eventId, address, lang));
    addToast(
      res?.registered
        ? t('dashboard.em.invite.sent_registered', { email: address })
        : t('dashboard.em.invite.sent_new', { email: address }),
      'success'
    );
    await load();
  };

  const submit = async (e) => {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError(t('dashboard.em.invite.err_INVALID_EMAIL'));
      return;
    }
    setError(null);
    setSending(true);
    try {
      await send(trimmed);
      setEmail('');
    } catch (err) {
      setError(errorMessage(t, err?.response?.data));
    } finally {
      setSending(false);
    }
  };

  const resend = async (invite) => {
    setResendingId(invite.id);
    try {
      await send(invite.email);
    } catch (err) {
      addToast(errorMessage(t, err?.response?.data), 'error');
    } finally {
      setResendingId(null);
    }
  };

  const { invites, canInvite, blocker } = data;
  const valid = EMAIL_RE.test(email.trim());

  return (
    <>
      <form
        onSubmit={submit}
        className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5 mb-5"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-brand-soft flex items-center justify-center shrink-0">
            <EnvelopeIcon className="w-5 h-5 text-brand-deep" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900">
              {t('dashboard.em.invite.title')}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              {t('dashboard.em.invite.subtitle')}
            </p>
          </div>
        </div>

        {!canInvite && blocker ? (
          <p className="text-sm rounded-xl bg-amber-50 text-amber-800 px-4 py-3">
            {t(`dashboard.em.invite.err_${blocker.code}`, blocker.message)}
          </p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                inputMode="email"
                autoComplete="off"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={t('dashboard.em.invite.placeholder')}
                aria-invalid={Boolean(error)}
                className={cx(
                  'flex-1 min-w-0 px-3 py-2.5 rounded-lg ring-1 outline-none text-sm bg-white',
                  error
                    ? 'ring-rose-300 focus:ring-2 focus:ring-rose-400'
                    : 'ring-gray-200 focus:ring-2 focus:ring-brand'
                )}
              />
              <button
                type="submit"
                disabled={!valid || sending}
                className={cx(
                  'px-4 py-2.5 text-sm font-semibold rounded-lg transition inline-flex items-center justify-center gap-2 shrink-0',
                  valid && !sending
                    ? 'bg-gray-900 text-white hover:bg-black'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                )}
              >
                {sending ? (
                  <ClipLoader color="#9ca3af" size={14} />
                ) : (
                  <PaperAirplaneIcon className="w-4 h-4" />
                )}
                {t('dashboard.em.invite.send')}
              </button>
            </div>
            {error && (
              <p className="text-xs text-rose-600 mt-2" role="alert">
                {error}
              </p>
            )}
          </>
        )}
      </form>

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900">
          {t('dashboard.em.invite.sent_title')}
        </h3>
        <span className="text-xs text-gray-400">
          {t('dashboard.em.invite.sent_count', { count: invites.length })}
        </span>
      </div>

      {loading && !invites.length ? (
        <div className="flex justify-center items-center py-16">
          <PulseLoader color="#FFBD59" size={10} />
        </div>
      ) : invites.length === 0 ? (
        <div className="text-center py-14 rounded-2xl bg-white ring-1 ring-black/5">
          <p className="text-sm font-semibold text-gray-900">
            {t('dashboard.em.invite.empty_title')}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {t('dashboard.em.invite.empty_body')}
          </p>
        </div>
      ) : (
        <ul className="rounded-2xl bg-white ring-1 ring-black/5 divide-y divide-gray-100">
          {invites.map((inv) => (
            <li
              key={inv.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {inv.email}
                </p>
                <p className="text-xs text-gray-400">
                  {t('dashboard.em.invite.sent_ago', {
                    when: moment(inv.lastSentAt).fromNow(),
                  })}
                  {inv.sendCount > 1 &&
                    ` · ${t('dashboard.em.invite.sent_times', { count: inv.sendCount })}`}
                </p>
              </div>
              <span
                className={cx(
                  'px-2.5 py-1 rounded-full text-[11px] font-semibold',
                  STATUS_STYLE[inv.status] || STATUS_STYLE.sent
                )}
              >
                {t(`dashboard.em.invite.status_${inv.status}`)}
              </span>
              {inv.canResend && canInvite && (
                <button
                  type="button"
                  onClick={() => resend(inv)}
                  disabled={resendingId === inv.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  {resendingId === inv.id ? (
                    <ClipLoader color="#6b7280" size={12} />
                  ) : (
                    t('dashboard.em.invite.resend')
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
